import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  bootstrap,
  getDecoded,
  getJwt,
  isExpired,
  onChange,
  refresh,
} from '@/lib/auth';
import {
  destroyLocal,
  getPouchDB,
  startSync,
  type SyncHandle,
} from '@/lib/couch';
import { initStoreFromPouchDB } from '@/lib/init-store';
import { useT } from '@/lib/i18n';
import { useApp } from '@/store';

interface Props {
  children: React.ReactNode;
}

enum Phase {
  LOADING = 'loading',
  AUTHED = 'authed',
  UNAUTHED = 'unauthed',
}

export function AuthGate({ children }: Props) {
  const location = useLocation();
  const t = useT();
  const [phase, setPhase] = useState<Phase>(Phase.LOADING);
  const syncRef = useRef<SyncHandle | null>(null);
  const prevSubRef = useRef<string | null>(null);
  const prevTenantRef = useRef<string | null>(null);
  const initFiredRef = useRef<string | null>(null);
  const changesRef = useRef<{ cancel: () => void } | null>(null);
  const rehydrateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const jwt = getJwt();
      const decoded = getDecoded();
      if (jwt && decoded && !isExpired(decoded)) {
        if (!cancelled) setPhase(Phase.AUTHED);
        return;
      }
      const decodedAfter = await bootstrap();
      if (cancelled) return;
      setPhase(decodedAfter ? Phase.AUTHED : Phase.UNAUTHED);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const off = onChange(() => {
      const decoded = getDecoded();
      if (!decoded) {
        setPhase(Phase.UNAUTHED);
        return;
      }
      if (isExpired(decoded)) {
        refresh().catch(() => setPhase(Phase.UNAUTHED));
        return;
      }
      setPhase(Phase.AUTHED);
    });
    return off;
  }, []);

  useEffect(() => {
    if (phase !== Phase.AUTHED) {
      // Re-arm the loading state so the next login shows skeletons until the
      // store re-hydrates from PouchDB, and allow the one-shot init to re-fire.
      useApp.getState().setHydrated(false);
      initFiredRef.current = null;
      if (syncRef.current) {
        syncRef.current.stop();
        syncRef.current = null;
      }
      const decoded = getDecoded();
      const prevSub = prevSubRef.current;
      if (prevSub && decoded?.sub !== prevSub) {
        // user changed or logged out — wipe local cache
        void destroyLocal();
      }
      prevSubRef.current = decoded?.sub ?? null;
      return;
    }
    const decoded = getDecoded();
    const prevSub = prevSubRef.current;
    const prevTenant = prevTenantRef.current;
    const curTenant = decoded?.tenant_id ?? null;
    if (prevSub && decoded && decoded.sub !== prevSub) {
      void destroyLocal();
    } else if (prevTenant && curTenant && prevTenant !== curTenant) {
      // Tenant changed (impersonation start/exit) — wipe local cache and
      // restart sync against the new tenant scope.
      void destroyLocal();
      if (syncRef.current) {
        syncRef.current.stop();
        syncRef.current = null;
      }
    }
    prevSubRef.current = decoded?.sub ?? null;
    prevTenantRef.current = curTenant;
    if (!syncRef.current) {
      syncRef.current = startSync();
    }
    // One-shot hydrate Zustand from local PouchDB after sync starts (or
    // restart after tenant switch). PouchDB returns whatever has replicated
    // so far — empty on first run leaves the seed/persisted state intact.
    const initKey = `${decoded?.sub ?? ''}|${curTenant ?? ''}`;
    if (initKey && initFiredRef.current !== initKey) {
      initFiredRef.current = initKey;
      void initStoreFromPouchDB().then(partial => {
        if (Object.keys(partial).length) {
          useApp.setState(partial as never);
        }
        // First hydrate attempt resolved — drop the loading skeletons even if
        // empty (fresh/empty tenant), live updates keep filling the store.
        useApp.getState().setHydrated(true);
      });
      // Subscribe to local PouchDB changes so docs that arrive AFTER the
      // initial hydration (typical on first login when replication is still
      // catching up) get pulled into the store. Debounced + best-effort —
      // failures (e.g. local DB destroyed during teardown) are swallowed.
      // Use `since: 0` (not 'now') so docs that already landed during the
      // race between startSync and this listener attach also fire a change
      // and trigger a rehydrate.
      if (changesRef.current) {
        try {
          changesRef.current.cancel();
        } catch {
          /* ignore */
        }
        changesRef.current = null;
      }
      try {
        const local = getPouchDB();
        const feed = local
          .changes({ since: 0, live: true, include_docs: false })
          .on('change', () => {
            if (rehydrateTimerRef.current)
              clearTimeout(rehydrateTimerRef.current);
            rehydrateTimerRef.current = setTimeout(() => {
              void initStoreFromPouchDB().then(partial => {
                if (!Object.keys(partial).length) return;
                // CAUTION: never blindly overwrite slices the FE actively
                // writes to between renders (customers, hoses, etc.) —
                // shadow-puts to PouchDB are async + fire-and-forget, so an
                // overwrite mid-flight would clobber pending mutations and
                // unmount mid-edit pages (CustomerDetail / HoseEdit /
                // ExtinguisherEdit all bail to "not found" when the customer
                // disappears from the store). The catalog slices below are
                // read-mostly during the normal user flow; reads are safe.
                const safe: typeof partial = {};
                if (partial.manufacturers)
                  safe.manufacturers = partial.manufacturers;
                if (partial.deviceTypes) safe.deviceTypes = partial.deviceTypes;
                if (partial.spareParts) safe.spareParts = partial.spareParts;
                if (partial.servicePrices)
                  safe.servicePrices = partial.servicePrices;
                if (partial.workers) safe.workers = partial.workers;
                if (partial.settings) safe.settings = partial.settings;
                if (partial.hoseConfig) safe.hoseConfig = partial.hoseConfig;
                if (Object.keys(safe).length) {
                  useApp.setState(safe as never);
                }
              });
            }, 250);
          })
          .on('error', () => {
            // Replication / DB tear-down may close the feed; ignore.
          });
        changesRef.current = { cancel: () => feed.cancel() };
      } catch {
        /* ignore — local DB may be unavailable */
      }
    }
    return () => {
      // keep sync alive across re-renders; teardown happens on unauth or unmount
    };
  }, [phase]);

  useEffect(() => {
    return () => {
      if (syncRef.current) {
        syncRef.current.stop();
        syncRef.current = null;
      }
      if (changesRef.current) {
        try {
          changesRef.current.cancel();
        } catch {
          /* ignore */
        }
        changesRef.current = null;
      }
      if (rehydrateTimerRef.current) {
        clearTimeout(rehydrateTimerRef.current);
        rehydrateTimerRef.current = null;
      }
    };
  }, []);

  if (phase === Phase.LOADING) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        {t('auth.loading')}
      </div>
    );
  }
  if (phase === Phase.UNAUTHED) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
